'use client';
import { type DependencyList, useEffect, useRef, useState } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  InfiniteLoader,
  type MasonryCellProps,
  Masonry as VirtualizedMasonry,
  WindowScroller,
} from 'react-virtualized';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { Loader } from '@/app/components/ui/loader';
import { SEARCH_EMPTY_ICON } from '@/app/constants';
import { useExpose, useMasonry } from '@/app/hooks';
import type { ElementType, LoadingState, PostReferenceItem } from '@/app/types';
import { cn } from '@/app/utils';
import { createPositioner } from '@/app/utils/client';
import { PostReferenceCard } from './PostReferenceCard';

const GRID_PARAMS = {
  spacer: 8,
};
const effectivePreset = 'activity';
/**
 * 检查行是否已加载
 */
const isRowLoaded = (elements: ElementType[]) => {
  return ({ index }: { index: number }) => {
    return !!elements[index];
  };
};

interface PostReferencePanelProps {
  className?: string;
  disabledIds?: string[];
  elements: ElementType[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  toggleElementSelection?: (element: ElementType) => void;
  deeps?: DependencyList;
  onExpose?: (idx: number) => void;
  scrollElementId?: string;
  onDelete?: (ids: string[]) => void;
}

const transformElement2PostReferenceItem = (element: ElementType): PostReferenceItem => {
  return {
    element: element.element,
    share: element.share,
  };
};

export const PostReferencePanel = ({
  className,
  disabledIds,
  elements,
  isLoading = false,
  hasNextPage = false,
  onLoadMore,
  toggleElementSelection,
  deeps,
  onExpose,
  scrollElementId = 'element-selector-scroll',
  onDelete,
}: PostReferencePanelProps) => {
  const { isMounted, scrollElement, masonryRef, cellPositioner, onResize, cache } = useMasonry({
    scrollElementId: scrollElementId,
    columnsPreset: effectivePreset,
  });
  const [cellLoadingStates, setCellLoadingStates] = useState<LoadingState>({});
  const exposureRefs = useRef<HTMLDivElement[]>([]);

  const { initObservedIndices } = useExpose(
    (idx) => {
      onExpose?.(idx);
    },
    exposureRefs.current,
    elements,
    [0.1, 1],
  );

  useEffect(() => {
    initObservedIndices();
  }, [deeps]);

  // 修改加载更多处理函数
  const loadMoreRows = async () => {
    if (hasNextPage && !isLoading) {
      // 设置相关单元格的加载状态
      const startIndex = elements.length;
      const endIndex = startIndex + 5; // 假设每次加载5个
      const newLoadingStates: Record<number, boolean> = {};
      for (let i = startIndex; i <= endIndex; i++) {
        newLoadingStates[i] = true;
      }
      setCellLoadingStates((prev) => ({ ...prev, ...newLoadingStates }));

      try {
        await onLoadMore?.();
      } finally {
        // 加载完成后清除加载状态
        setCellLoadingStates((prev) => {
          const newState = { ...prev };
          for (let i = startIndex; i <= endIndex; i++) {
            delete newState[i];
          }
          return newState;
        });
      }
    }
  };

  // 初始加载或无滚动元素时显示 loading
  if (!isMounted || !scrollElement || (!elements.length && isLoading)) {
    return (
      <div className={cn('flex h-full w-full items-center justify-center', className)}>
        <Loader />
      </div>
    );
  }

  // 空状态
  if (!elements.length) {
    return (
      <div className={cn('flex h-full w-full items-center justify-center', className)}>
        <SearchEmpty emptySrc={SEARCH_EMPTY_ICON.selector} textClassName="text-xs" />
      </div>
    );
  }

  const { spacer } = GRID_PARAMS;

  const cellRenderer = ({ index, key, parent, style }: MasonryCellProps) => {
    // 创建唯一的 key
    const cellKey = `${String(key)}-${index}`;

    // 获取实际的资产索引
    const itemIndex = index;
    const element = elements[itemIndex];

    // 如果 item 为空或者正在加载中，显示骨架屏
    if (!element || cellLoadingStates[index]) {
      return (
        <CellMeasurer cache={cache.current} index={index} key={cellKey} parent={parent}>
          <div style={style}>
            <div className="relative mb-1 w-full">
              <div className="aspect-video w-full animate-pulse rounded-8 bg-gray-300/40" />
            </div>
          </div>
        </CellMeasurer>
      );
    }
    const checked = disabledIds?.includes(element.id || '');
    return (
      <CellMeasurer cache={cache.current} index={index} key={cellKey} parent={parent}>
        <div
          style={style}
          ref={(el: HTMLDivElement) => {
            exposureRefs.current[index] = el;
          }}
          data-index={itemIndex}
        >
          <PostReferenceCard
            data={transformElement2PostReferenceItem(element)}
            className={cn('border border-transparent transition-colors', checked && 'border-2 border-system-blue02')}
            onClick={() => {
              toggleElementSelection?.(element);
            }}
            onDelete={onDelete}
          />
        </div>
      </CellMeasurer>
    );
  };

  // 请求数据时，显示loading
  if (!isMounted || !scrollElement || (!elements.length && isLoading)) {
    return (
      <div className={cn('flex size-full flex-1 items-center justify-center')}>
        <Loader />
      </div>
    );
  }

  if (!elements.length) {
    return <div className={cn('flex size-full flex-1 items-center justify-center')}>111</div>;
  }

  return (
    <WindowScroller scrollElement={scrollElement}>
      {({ height, scrollTop }) => (
        <InfiniteLoader
          isRowLoaded={isRowLoaded(elements)}
          loadMoreRows={loadMoreRows}
          rowCount={hasNextPage ? elements.length + 1 : elements.length}
          threshold={5}
        >
          {({ onRowsRendered }) => (
            <AutoSizer disableHeight onResize={onResize}>
              {({ width }) => {
                if (!cellPositioner.current) {
                  cellPositioner.current = createPositioner({
                    width,
                    cache: cache.current,
                    columnsPreset: effectivePreset,
                    spacer: spacer,
                  });
                }
                return (
                  <VirtualizedMasonry
                    autoHeight
                    ref={masonryRef}
                    cellCount={elements.length}
                    cellMeasurerCache={cache.current}
                    cellPositioner={cellPositioner.current}
                    cellRenderer={cellRenderer}
                    height={height}
                    width={width}
                    overscanByPixels={400}
                    scrollTop={scrollTop}
                    key={`masonry-${width}`}
                    onCellsRendered={({ stopIndex }) => {
                      onRowsRendered({
                        startIndex: 0,
                        stopIndex,
                      });
                    }}
                  />
                );
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      )}
    </WindowScroller>
  );
};
